import { useState } from 'react';
import { ChevronDown, CheckCircle2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface Module {
  title: string;
  duration?: string;
  content: string[];
}

interface FormationProgramProps {
  program: Module[];
}

export function FormationProgram({ program }: FormationProgramProps) {
  const { t } = useTranslation('formations');
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const toggleAccordion = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  // Custom SVG icons for each module (cycle through them)
  const getModuleIcon = (index: number) => {
    const icons = [
      // Icon 1: Foundation/Introduction
      <svg viewBox="0 0 100 100" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
        <rect x="20" y="60" width="60" height="25" fill="#F06C6E" opacity="0.3" rx="2"/>
        <rect x="30" y="45" width="40" height="15" fill="#F06C6E" opacity="0.5" rx="2"/>
        <rect x="40" y="30" width="20" height="15" fill="#F06C6E" opacity="0.7" rx="2"/>
        <path d="M50 15 L60 30 L40 30 Z" fill="#F06C6E"/>
        <circle cx="50" cy="18" r="3" fill="white"/>
      </svg>,

      // Icon 2: Tools/Features
      <svg viewBox="0 0 100 100" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
        <rect x="25" y="25" width="50" height="50" fill="none" stroke="#F06C6E" strokeWidth="3" rx="4"/>
        <line x1="50" y1="25" x2="50" y2="75" stroke="#F06C6E" strokeWidth="2"/>
        <line x1="25" y1="50" x2="75" y2="50" stroke="#F06C6E" strokeWidth="2"/>
        <circle cx="50" cy="50" r="8" fill="#F06C6E"/>
        <circle cx="50" cy="50" r="4" fill="white"/>
      </svg>,

      // Icon 3: Practice/Hands-on
      <svg viewBox="0 0 100 100" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
        <circle cx="50" cy="35" r="8" fill="#F06C6E" opacity="0.6"/>
        <path d="M50 43 L50 60" stroke="#F06C6E" strokeWidth="4" opacity="0.6"/>
        <path d="M50 55 L35 70" stroke="#F06C6E" strokeWidth="4" opacity="0.6"/>
        <path d="M50 55 L65 70" stroke="#F06C6E" strokeWidth="4" opacity="0.6"/>
        <circle cx="35" cy="70" r="5" fill="#F06C6E"/>
        <circle cx="65" cy="70" r="5" fill="#F06C6E"/>
        <path d="M35 50 L42 50 L42 65" stroke="#F06C6E" strokeWidth="3.5" strokeLinecap="round"/>
        <path d="M65 50 L58 50 L58 65" stroke="#F06C6E" strokeWidth="3.5" strokeLinecap="round"/>
      </svg>,

      // Icon 4: Analysis/Advanced
      <svg viewBox="0 0 100 100" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
        <rect x="25" y="60" width="10" height="25" fill="#F06C6E" opacity="0.6"/>
        <rect x="42" y="45" width="10" height="40" fill="#F06C6E" opacity="0.7"/>
        <rect x="59" y="30" width="10" height="55" fill="#F06C6E" opacity="0.8"/>
        <path d="M30 60 L47 45 L64 30" stroke="#F06C6E" strokeWidth="3" fill="none" strokeLinecap="round"/>
        <circle cx="30" cy="60" r="4" fill="#F06C6E"/>
        <circle cx="47" cy="45" r="4" fill="#F06C6E"/>
        <circle cx="64" cy="30" r="4" fill="#F06C6E"/>
      </svg>,

      // Icon 5: Project/Application
      <svg viewBox="0 0 100 100" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
        <rect x="25" y="35" width="50" height="40" fill="none" stroke="#F06C6E" strokeWidth="3" rx="3"/>
        <rect x="25" y="35" width="50" height="10" fill="#F06C6E" opacity="0.6"/>
        <line x1="35" y1="55" x2="50" y2="55" stroke="#F06C6E" strokeWidth="2" opacity="0.5"/>
        <line x1="35" y1="62" x2="65" y2="62" stroke="#F06C6E" strokeWidth="2" opacity="0.5"/>
        <rect x="57" y="50" width="10" height="10" fill="#F06C6E" opacity="0.7"/>
      </svg>,

      // Icon 6: Export/Output
      <svg viewBox="0 0 100 100" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
        <rect x="30" y="25" width="40" height="50" fill="none" stroke="#F06C6E" strokeWidth="3" rx="2"/>
        <path d="M45 45 L50 40 L55 45" stroke="#F06C6E" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
        <line x1="50" y1="40" x2="50" y2="60" stroke="#F06C6E" strokeWidth="3" strokeLinecap="round"/>
        <line x1="35" y1="65" x2="65" y2="65" stroke="#F06C6E" strokeWidth="2" opacity="0.5"/>
      </svg>
    ];

    return icons[index % icons.length];
  };

  return (
    <section className="py-24 bg-transparent">
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-heading font-extrabold text-aspexit-black mb-4 text-center">
            {t('program.title')}
          </h2>
          <p className="text-center text-aspexit-gray mb-12 max-w-2xl mx-auto">
            {t('program.subtitle', { subject: 'QGIS' })}
          </p>

          <div className="space-y-4">
            {program.map((module, index) => (
              <div
                key={index}
                className={`bg-white rounded-2xl overflow-hidden transition-all duration-300 ${
                  openIndex === index
                    ? 'border-2 border-aspexit-red shadow-xl'
                    : 'border-2 border-border hover:border-aspexit-red/40 hover:shadow-lg'
                }`}
              >
                {/* Accordion Header */}
                <button
                  onClick={() => toggleAccordion(index)}
                  className="w-full flex items-center justify-between p-6 hover:bg-aspexit-red/5 transition-colors"
                >
                  <div className="flex items-center gap-5 text-left">
                    {/* Custom SVG Icon */}
                    <div className="w-16 h-16 flex-shrink-0 bg-gradient-to-br from-aspexit-red/10 to-aspexit-orange/10 rounded-xl p-3 shadow-sm">
                      {getModuleIcon(index)}
                    </div>
                    <div>
                      <div className="flex items-center gap-3 mb-1">
                        <span className="text-xs font-bold text-aspexit-red bg-aspexit-red/10 px-2 py-1 rounded">
                          {t('program.module', { number: index + 1 })}
                        </span>
                        {module.duration && (
                          <span className="text-xs text-aspexit-gray">
                            ⏱️ {module.duration}
                          </span>
                        )}
                      </div>
                      <h3 className="text-xl font-heading font-bold text-aspexit-black">
                        {module.title}
                      </h3>
                    </div>
                  </div>
                  <ChevronDown
                    className={`w-6 h-6 text-aspexit-red transition-transform flex-shrink-0 ${
                      openIndex === index ? 'rotate-180' : ''
                    }`}
                  />
                </button>

                {/* Accordion Content */}
                {openIndex === index && (
                  <div className="px-6 pb-6 border-t border-border bg-aspexit-red/5">
                    <div className="pt-6">
                      {/* Icon SVG */}
                      <div className="flex items-start gap-6">
                        <div className="w-16 h-16 flex-shrink-0 bg-white rounded-xl p-3 shadow-sm">
                          {getModuleIcon(index)}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-aspexit-red mb-4 flex items-center gap-2">
                            <span className="w-1.5 h-1.5 bg-aspexit-red rounded-full"></span>
                            {t('program.moduleContent')}
                          </p>
                          <ul className="space-y-3">
                            {module.content.map((item, itemIndex) => (
                              <li
                                key={itemIndex}
                                className="flex items-start gap-3"
                              >
                                <CheckCircle2 className="w-5 h-5 text-aspexit-red flex-shrink-0 mt-0.5" />
                                <span className="text-aspexit-gray leading-relaxed">{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
